import { useContext, useEffect, useState } from 'react'
import { NavLink, NavLinkProps, useLocation } from 'react-router-dom'
import { useAuthenticator } from '@aws-amplify/ui-react'
import prodLogo from '../../../assets/images/trmi-logos/prod.png'
import trainLogo from '../../../assets/images/trmi-logos/train.png'
import qaLogo from '../../../assets/images/trmi-logos/qa.png'
import devLogo from '../../../assets/images/trmi-logos/dev.png'
import ephemeralLogo from '../../../assets/images/trmi-logos/ephemeral.png'
import localLogo from '../../../assets/images/trmi-logos/local.png'
import styles from './styles.module.css'
import ToastNotification from '../../../global/components/toast-alert/ToastNotification'
import { ToastNotificationContext } from '../../../global/context/toast-context/ToastNotificationContext'
import {
  IS_ENV_DEV,
  IS_ENV_EPHEMERAL,
  IS_ENV_LOCAL,
  IS_ENV_QA,
  IS_ENV_TRAIN,
} from '../../../global/constants/env'
import { UserContext } from '../base-container/BaseContainer'
import { getISOWeekNumber } from '../../../global/utils/date/get-iso-week-number'
import { IncidentReportCountContext } from '../../../global/context/incident-report-count-context/IncidentReportCountContext'
import { TITLES } from '../../../global/constants/nav-bar-links'

interface LinksProps {
  links: Array<NavLinkProps>
}

const logo = (() => {
  if (IS_ENV_TRAIN) {
    return trainLogo
  }
  if (IS_ENV_QA) {
    return qaLogo
  }
  if (IS_ENV_DEV) {
    return devLogo
  }
  if (IS_ENV_EPHEMERAL) {
    return ephemeralLogo
  }
  if (IS_ENV_LOCAL) {
    return localLogo
  }

  return prodLogo
})()

function Links(props: LinksProps) {
  const { links } = props

  const { signOut } = useAuthenticator((context) => [context.signOut])

  const logOut = () => {
    signOut()
  }

  const { pathname } = useLocation()
  const [selected, setSelected] = useState(pathname)
  const { outstandingCount } = useContext(IncidentReportCountContext)

  useEffect(() => {
    setSelected(`/${pathname.split('/')[1]}`)
  }, [pathname])

  return (
    <div className={styles.linksHolder}>
      {links.map((link: NavLinkProps, idx: number) => {
        const linkToBase = link.to.toString().split('/')[1]
        const selectedBase = selected.toString().split('/')[1]
        const isTabHighlighted = linkToBase === selectedBase
        // "(#)" of the user's outstanding (In Progress) incident reports
        const showOutstandingCount =
          link.title === TITLES.INCIDENT_REPORTS && outstandingCount > 0
        const label = showOutstandingCount
          ? `${link.title} (${outstandingCount})`
          : link.title
        return (
          <NavLink
            className={`${styles.link} ${
              isTabHighlighted ? `${styles.chosen} font--bold` : ''
            }`}
            key={idx}
            to={link.to}
          >
            {label}
          </NavLink>
        )
      })}
      <div
        onClick={logOut}
        className={styles.link}
      >
        Log Out
      </div>
    </div>
  )
}

interface NavDrawerProps {
  links: Array<NavLinkProps>
}

function NavDrawer(props: NavDrawerProps) {
  const { toastStatus, handleClose } = useContext(ToastNotificationContext)
  const { user } = useContext(UserContext)
  const weekNumber = getISOWeekNumber(new Date())
  const { links } = props

  return (
    <>
      <ToastNotification
        severity={toastStatus.severity}
        open={toastStatus.isOpen}
        handleClose={handleClose}
        message={toastStatus.message}
      />
      <div className='navDrawerMain'>
        <div className={styles.navDrawerContainer}>
          <div className={styles.header}>
            <div className={styles.logoHolder}>
              <img
                src={logo}
                className={styles.logo}
                alt='logo'
              />
            </div>
            <div className={styles.headerInfo}>
              <div className='title font--bold font--large'>CS Portal</div>
              <div className='weekNumber'>Week {weekNumber}</div>
            </div>
          </div>
          <div>
            <Links links={links} />
          </div>
          {user && (
            <div className={styles.userContainer}>
              <div className={styles.userName}>{user?.name || ''}</div>
              <div className={styles.userInfo}>{user?.email || ''}</div>
              <div className={styles.userInfo}>{user?.role?.name || ''}</div>
            </div>
          )}
        </div>
      </div>
    </>
  )
}

export default NavDrawer
